import { ACTION_DESKTOP_NAV_HEADER, CATEGORY_NAV } from 'utils/analytics/eventTypesConstants';
import {
  ACTION_GO_TO_LOGIN,
  ACTION_GO_TO_SIGNUP,
  ACTION_NAVIGATE_TO_JOIN,
} from 'utils/analytics/eventTypeConstantsAmplitude';
import {
  AuxiliaryContent,
  DesktopRightContent,
  HeaderBaseProps,
  Text,
  defaultSlimHeaderSpecForContent,
  transformRightContentIntoInfo,
} from 'omakase/Header/Header';
import { AvatarButton, Button, IconButton, Popover, TockLogoAnchor } from 'omakase';
import { DEFAULT_PROFILE_IMAGE_URL } from 'omakase/SiteMenuTopLevelHeading/SiteMenuTopLevelHeading';
import { DesktopSiteMenuImpl } from 'components/site-menu/DesktopSiteMenuImpl';
import { IconArrowLeft, IconExternalLink, IconMap } from '@tock/icons';
import { ShrinkButtonsToIconsContext } from 'omakase/Button/ShrinkingHeaderButton';
import { SiteInfo } from 'components/header/SiteInfo';
import { Theme, css } from '@emotion/react';
import { computeLogInHref, computeSignupHref } from '../../utils/logInSignUpHrefUtils';
import {
  desktopHeaderSlimLeft,
  desktopHeaderSlimRight,
  omakaseHeaderAuthenticationButtons,
  omakaseHeaderCenterItems,
  omakaseHeaderDesktopContainer,
  omakaseHeaderDesktopMain,
  omakaseHeaderLeftItems,
  omakaseHeaderLeftmostItemDivider,
  omakaseHeaderRightItems,
  omakaseHeaderShadow,
} from './headerCssDefs';
import { navigationSelector } from 'selectors/navigationSelector';
import { useAmplitudeSendEvent } from 'utils/analytics/AnalyticsContext';
import { useHeaderScrollShadow } from 'omakase/Header/useHeaderScrollShadow';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Anchor from 'components/Anchor';
import React from 'react';

export interface DesktopHeaderProps extends HeaderBaseProps {
  rightAuxiliaryContent: AuxiliaryContent;
  rightContent: DesktopRightContent;
  includeLeftwardDivider?: boolean;
  isStorybook?: boolean;
}

const DesktopHeader = ({
  auxiliaryContent,
  additionalContent,
  centerContent,
  hasShadow,
  leftContent,
  rightAuxiliaryContent,
  rightContent,
  bumpZIndex,
  includeLeftwardDivider = true,
  slimLeft = 'defaultForContent',
  slimRight = 'defaultForContent',
  isStorybook = false,
  ...rest
}: DesktopHeaderProps) => {
  const amplitudeSendEvent = useAmplitudeSendEvent();
  const showShadow = useHeaderScrollShadow(hasShadow);
  const { pathname, search } = useLocation();
  const isHome = pathname === '/';
  const { isBusinessProfile } = useSelector(navigationSelector);

  const baseUrl = 'https://resy.com/join/get-started/';
  const href = search ? `${baseUrl}${search}` : baseUrl;

  let leftItems;
  let centerItem: React.ReactElement | undefined;
  const rightItems: React.ReactNode[] = [];

  // The header itself manages whether the account menu is showing.
  const [isMenuOpen, setIsMenuOpen] = React.useState<HTMLElement | null>(null);
  const handleAccountMenuOpen = React.useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setIsMenuOpen(event.currentTarget);
    window.analytics?.sendEvent(
      CATEGORY_NAV,
      ACTION_DESKTOP_NAV_HEADER,
      'Menu opened with avatar button'
    );
  }, []);
  const handleAccountMenuClose = React.useCallback(() => {
    setIsMenuOpen(null);
  }, []);

  if ('isTakeover' in centerContent && centerContent.isTakeover) {
    centerItem = centerContent.button;
  } else {
    // For desktop rendering, the auxiliary content is laid out just after the
    // left content (if any), so let's check this first.
    switch (auxiliaryContent.type) {
      case 'AuxiliaryButtons':
        leftItems = React.Children.toArray(auxiliaryContent.buttons);
        break;
      default:
        leftItems = React.Children.toArray([]);
        break;
    }

    let leftmostItem;
    switch (leftContent.type) {
      case 'TockLogo':
        leftmostItem = (
          <React.Fragment key="tock-logo-group">
            <TockLogoAnchor
              aria-label={leftContent.config?.['aria-label'] ?? 'Tock home page'}
              href={leftContent.config?.href ?? '/'}
            />
            {!isStorybook && <SiteInfo />}
          </React.Fragment>
        );
        break;
      case 'LeftButton':
        leftmostItem = (
          <IconButton
            aria-label="Back"
            component="button"
            onClick={leftContent.onClick}
            size="medium"
            variant="ghost"
            key="left_backbutton"
          >
            <IconArrowLeft />
          </IconButton>
        );
        break;
      case 'MapButton':
        leftmostItem = (
          <IconButton
            aria-label="Map"
            component="button"
            onClick={leftContent.onClick}
            size="medium"
            variant="ghost"
            key="left_mapbutton"
          >
            <IconMap />
          </IconButton>
        );
        break;
      case 'TitleText':
        leftmostItem = <Text text={leftContent.text} key="left_text" />;
        break;
    }

    if (leftmostItem && auxiliaryContent.type === 'AuxiliaryButtons' && includeLeftwardDivider) {
      const shrinkMarginsAtMediumWidth =
        rightContent.type === 'AvatarOrAuthenticationButtons' && !rightContent.isAuthenticated;

      leftItems.unshift(
        <div
          key="divider"
          css={(theme: Theme) =>
            omakaseHeaderLeftmostItemDivider(
              theme,
              shrinkMarginsAtMediumWidth,
              !!auxiliaryContent.slimLeft
            )
          }
        />
      );
    }

    if (leftmostItem) {
      leftItems.unshift(leftmostItem);
    }

    switch (centerContent.type) {
      case 'TitleText':
        centerItem = <Text text={centerContent.text} />;
        break;
      case 'TockLogo':
        centerItem = (
          <TockLogoAnchor
            aria-label={centerContent.config?.['aria-label'] ?? 'Tock home page'}
            href={centerContent.config?.href ?? '/'}
          />
        );
        break;
      case 'CenterButton':
        centerItem = centerContent.button;
        break;
    }

    if (rightAuxiliaryContent.type === 'AuxiliaryButtons') {
      rightItems.push(...React.Children.toArray(rightAuxiliaryContent.buttons));
    }

    if (rightContent.type === 'AvatarOrAuthenticationButtons') {
      const {
        isAuthenticated,
        isPartner,
        guestAccountInfo: { profileImageUrl, name } = {
          profileImageUrl: undefined,
          name: undefined,
        },
        pathname,
      } = rightContent;

      rightItems.push(
        isAuthenticated ? (
          <React.Fragment key="avatar-or-auth-buttons">
            <AvatarButton
              alt="Profile image"
              aria-label="Toggle menu open"
              aria-haspopup
              onClick={handleAccountMenuOpen}
              selected={!!isMenuOpen}
              size="medium"
              src={profileImageUrl ?? DEFAULT_PROFILE_IMAGE_URL}
              data-testid="profile-button"
            />
            <Popover
              open={!!isMenuOpen}
              anchorEl={isMenuOpen}
              onClose={handleAccountMenuClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              minWidthPx={320}
            >
              <DesktopSiteMenuImpl
                isPartner={isPartner}
                onClose={handleAccountMenuClose}
                patronName={name}
                pathname={pathname}
                profileImageLink={profileImageUrl}
              />
            </Popover>
          </React.Fragment>
        ) : (
          <span key="auth-buttons" css={omakaseHeaderAuthenticationButtons}>
            {!isBusinessProfile && (
              <Button
                component={Anchor}
                variant="ghost"
                size="medium"
                iconEnd={<IconExternalLink />}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  amplitudeSendEvent(ACTION_NAVIGATE_TO_JOIN, {
                    pageRegion: 'Header',
                  });
                }}
              >
                Use Tock at your business
              </Button>
            )}
            <Button
              variant="primary"
              component={Anchor}
              onClick={() => {
                amplitudeSendEvent(ACTION_GO_TO_SIGNUP);
                window.analytics?.sendEvent(CATEGORY_NAV, ACTION_DESKTOP_NAV_HEADER, 'Sign up');
              }}
              size="medium"
              href={computeSignupHref(pathname)}
            >
              Sign up
            </Button>
            <Button
              variant="basic"
              component={Anchor}
              onClick={() => {
                amplitudeSendEvent(ACTION_GO_TO_LOGIN);
                window.analytics?.sendEvent(CATEGORY_NAV, ACTION_DESKTOP_NAV_HEADER, 'Log in');
              }}
              size="medium"
              href={computeLogInHref(pathname)}
            >
              Log in
            </Button>
          </span>
        )
      );
    } else {
      const rightItemInfo = transformRightContentIntoInfo(rightContent);
      if (rightItemInfo) {
        const { Icon, ariaLabel, isPrimary, onClick } = rightItemInfo;
        if (rightContent.type === 'SearchWideButton') {
          leftItems.push(
            <Button
              component="button"
              onClick={onClick}
              iconStart={<Icon />}
              size="medium"
              variant="primary"
              key={`right_${rightContent.type}`}
            >
              {ariaLabel}
            </Button>
          );
        } else {
          leftItems.push(
            <IconButton
              aria-label={ariaLabel}
              component="button"
              onClick={onClick}
              size="medium"
              variant={isPrimary ? 'primary' : 'ghost'}
              key={`right_${rightContent.type}`}
            >
              <Icon />
            </IconButton>
          );
        }
      }
    }
  }
  if (slimLeft === 'defaultForContent') {
    slimLeft = defaultSlimHeaderSpecForContent(leftContent, true);
  }
  if (slimRight === 'defaultForContent') {
    slimRight = defaultSlimHeaderSpecForContent(rightContent, true);
  }

  return (
    <div
      css={(theme: Theme) => [
        omakaseHeaderDesktopContainer(theme),
        omakaseHeaderShadow(theme, { showShadow }),
        isHome &&
          css`
            border-bottom: 1px solid ${theme.colors.borderDefault};
          `,
        bumpZIndex &&
          css`
            z-index: 4;
          `,
      ]}
    >
      <div
        css={(theme: Theme) => [
          omakaseHeaderDesktopMain(theme),
          slimLeft === 'yes' && desktopHeaderSlimLeft,
          slimRight === 'yes' && desktopHeaderSlimRight,
        ]}
        {...rest}
      >
        {/* We place the center-items first in the list because their div is explicitly
      sized to that of the centaining div. If we put it later it will intercept
      all mouse events. */}
        <div css={omakaseHeaderCenterItems}>{centerItem}</div>
        <ShrinkButtonsToIconsContext.Provider value={false}>
          <div css={omakaseHeaderLeftItems}>{leftItems}</div>
        </ShrinkButtonsToIconsContext.Provider>
        <div css={omakaseHeaderRightItems}>{rightItems}</div>
      </div>
      {additionalContent}
    </div>
  );
};

export default DesktopHeader;
